import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  Status,
  StatusCreate,
  StatusGetFiltersResponse,
  StatusGetResponse,
  StatusUpdate,
} from '../types/status.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';
import { useActiveTenant } from '../../../../../states/active-tenant.atom';

const useStatusBasePath = () => {
  const { activeTenant } = useActiveTenant();
  return useMemo(() => {
    return `/v1/admin/tenants/${activeTenant?.id}/statuses`;
  }, [activeTenant]);
};

export const useStatusHooks = () => {
  const statusBasePath = useStatusBasePath();
  const { activeTenant } = useActiveTenant();

  return createApiHooks<Status, StatusGetResponse, StatusCreate, StatusUpdate>({
    basePath: statusBasePath,
    queryKey: ['Statuses', 'Tenant', activeTenant!.id],
    apiClient,
  });
};

export const useStatusGetFilters = (queryOptions?: any) => {
  const statusBasePath = useStatusBasePath();
  return useGenericGet<StatusGetFiltersResponse>(
    `${statusBasePath}/filters`,
    ['Statuses', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the status hooks
export const useStatusApi = () => {
  const statusHooks = useStatusHooks();

  return {
    useGet: statusHooks.useGet,
    useCreate: statusHooks.useCreate,
    useUpdate: statusHooks.useUpdate,
    useDelete: statusHooks.useDelete,
    useList: (params: UseListParams) => statusHooks.useList(params),
  };
};

// Export individual hooks for backward compatibility
export const useStatusGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useStatusApi();
  return useGet(id, queryOptions);
};

export const useStatusCreate = (mutationOptions?: any) => {
  const { useCreate } = useStatusApi();
  return useCreate(mutationOptions);
};

export const useStatusUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useStatusApi();
  return useUpdate(mutationOptions);
};

export const useStatusDelete = (mutationOptions?: any) => {
  const { useDelete } = useStatusApi();
  return useDelete(mutationOptions);
};

export const useStatusList = (params: UseListParams) => {
  const { useList } = useStatusApi();
  return useList(params);
};
